/* Overlays: Dialog, Sheet (bottom sheet), Toast (provider + hook), Tooltip. */
import {
  cloneElement, createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode, type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { IconButton } from './basics';
import { cx } from './cx';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** Traps Tab inside `ref`, focuses the first field, restores focus on close, closes on Esc. */
function useModal(open: boolean, ref: React.RefObject<HTMLElement | null>, onClose: () => void, initialFocus?: string) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = (initialFocus && el?.querySelector<HTMLElement>(initialFocus)) || el?.querySelector<HTMLElement>('[autofocus],input,textarea') || el?.querySelector<HTMLElement>(FOCUSABLE) || el;
    first?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onCloseRef.current(); return; }
      if (e.key !== 'Tab' || !el) return;
      const list = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!list.length) return;
      const a = list[0]!, z = list[list.length - 1]!;
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); prev?.focus?.({ preventScroll: true }); };
  }, [open, ref, initialFocus]);
}

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** Position near the top (palettes, pickers) instead of centered. */
  top?: boolean;
  className?: string;
  initialFocus?: string;
  /** Hide the visible header (title stays as the accessible name). */
  bare?: boolean;
}
export function Dialog({ open, onClose, title, children, footer, top, className, initialFocus, bare }: DialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModal(open, ref, onClose, initialFocus);
  if (!open) return null;
  return createPortal(
    <div className={cx('scrim', top && 'top')} onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId} className={cx(!bare && 'dialog', className)} tabIndex={-1}>
        {bare ? <h2 id={titleId} className="sr-only">{title}</h2> : (
          <div className="dialog-hd"><h2 id={titleId}>{title}</h2><IconButton label="Close" icon={X} onClick={onClose} /></div>
        )}
        {children}
        {footer && <div className="dialog-ft">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Sheet({ open, onClose, title, children, className }: { open: boolean; onClose: () => void; title: string; children?: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModal(open, ref, onClose);
  if (!open) return null;
  return createPortal(
    <>
      <div className="sheet-scrim" aria-hidden="true" onPointerDown={onClose} />
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId} className={cx('sheet', className)} tabIndex={-1}>
        <div className="sheet-grab" aria-hidden="true" />
        <div className="sheet-title" id={titleId}>{title}</div>
        {children}
      </div>
    </>,
    document.body,
  );
}

/* Toast */
export interface ToastOptions { action?: { label: string; onClick: () => void }; tone?: 'neutral' | 'danger'; durationMs?: number }
interface ToastItem extends ToastOptions { id: number; message: ReactNode }
interface ToastApi { show: (message: ReactNode, opts?: ToastOptions) => number; dismiss: (id: number) => void }
const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((l) => l.filter((t) => t.id !== id)), []);
  const show = useCallback((message: ReactNode, opts: ToastOptions = {}) => {
    const id = ++seq.current;
    setItems((l) => [...l.slice(-2), { id, message, ...opts }]);
    // Undo windows stay 8 s (rule 19); plain notices 4 s.
    window.setTimeout(() => dismiss(id), opts.durationMs ?? (opts.action ? 8000 : 4000));
    return id;
  }, [dismiss]);
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={cx('toast', t.tone === 'danger' && 'danger')}>
            <span>{t.message}</span>
            {t.action && <button type="button" className="link" onClick={() => { t.action!.onClick(); dismiss(t.id); }}>{t.action.label}</button>}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

const noopToast: ToastApi = { show: () => 0, dismiss: () => undefined };
export function useToast(): ToastApi {
  return useContext(ToastCtx) ?? noopToast;
}

/** Tooltip: shows on hover and keyboard focus; the child gets aria-describedby. */
export function Tooltip({ content, children }: { content: ReactNode; children: ReactElement<{ 'aria-describedby'?: string }> }) {
  const [show, setShow] = useState(false);
  const id = useId();
  return (
    <span className="tip-wrap" onPointerEnter={() => setShow(true)} onPointerLeave={() => setShow(false)} onFocus={() => setShow(true)} onBlur={() => setShow(false)}
      onKeyDown={(e) => { if (e.key === 'Escape') setShow(false); }}>
      {cloneElement(children, { 'aria-describedby': id })}
      <span role="tooltip" id={id} className="tip" hidden={!show}>{content}</span>
    </span>
  );
}
